import type { LessonModel } from '../Models/LessonModel';
import type { StudentAbsenceModel, StudentSlotModel } from '../Models/StudentModel';
import { weekdayOf } from './LessonStatusUtil';

/**
 * A recurring slot with no lesson booked against it yet.
 *
 * A student's regular hour is a promise of a lesson, not a lesson row: the
 * row appears when the tutor books it, marks it given, or takes the money.
 * Until then the calendar and the dashboard still have to show that the hour
 * is coming, because that is what the tutor's day actually looks like.
 */
export interface PlannedLesson {
  student: string;
  date: string;
  time: string;
  durationMinutes: number;
}

/**
 * The recurring hours that fall on `date` and have no lesson row yet. A slot
 * whose hour already has a lesson that day is booked, not planned, and a
 * student away that day (a known absence) has no planned hour at all.
 */
export function plannedLessonsFor(
  date: string,
  slotsByStudent: ReadonlyMap<string, readonly StudentSlotModel[]>,
  absencesByStudent: ReadonlyMap<string, readonly StudentAbsenceModel[]>,
  actual: readonly Pick<LessonModel, 'student' | 'time'>[]
): PlannedLesson[] {
  const weekday = weekdayOf(date);
  const planned: PlannedLesson[] = [];
  for (const [student, slots] of slotsByStudent) {
    if (isAbsentOn(date, absencesByStudent.get(student) ?? [])) continue;
    for (const slot of slots) {
      if (Number(slot.weekday) !== weekday) continue;
      if (actual.some(lesson => lesson.student === student && lesson.time === slot.time)) continue;
      planned.push({ student, date, time: slot.time, durationMinutes: slot.durationMinutes });
    }
  }
  return planned.sort((a, b) => a.time.localeCompare(b.time));
}

/**
 * A recurring slot still exists during a known absence, it just should not
 * suggest a lesson: the range is a gap in the calendar, not a change to the
 * student's regular hour, which is why only the planned chip is filtered
 * rather than the slot itself being touched.
 */
export function isAbsentOn(date: string, absences: readonly StudentAbsenceModel[]): boolean {
  return absences.some(absence => date >= absence.startDate && date <= absence.endDate);
}
